import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { GeoObjectMeshOptions, GeoObjectMode, GeoObjectTypes, PreProcessingTypes, RawDataGroup } from '@visualizer/types';
import { GeoObject } from './GeoObject';
import { getOnlyGeometryFromRawData } from './utils';
import { getGlobalState } from '@gui/store/persistent/persistentStore';

export abstract class GeoObjectSingleMesh<T extends GeoObjectTypes> extends GeoObject<T> {
    protected meshes: THREE.Mesh;
    protected strokes: THREE.LineSegments;

    constructor({ name, colors, data, mode, thresholdAngle, types }: GeoObjectMeshOptions<T>) {
        super({ name, colors, mode, types });

        const meshes = this.initMeshes(data, thresholdAngle);

        this.meshes = meshes.meshes;
        this.strokes = meshes.strokes;
        this.strokes.renderOrder = 2;

        this.add(this.meshes);
        this.add(this.strokes);
    }

    override export(): { name: string; mesh: THREE.Mesh }[] {
        return [{ name: this.name, mesh: this.meshes.clone() }];
    }

    override hasIndividualMeshes(): boolean {
        return false;
    }

    override dispose() {
        super.dispose();
        this.meshes.geometry.dispose();
        this.strokes.geometry.dispose();
    }

    private initMeshes(data: RawDataGroup[], thresholdAngle = 10): { meshes: THREE.Mesh; strokes: THREE.LineSegments } {
        const geoms: THREE.BufferGeometry[][] = [];
        const materials = [];
        const strokeMaterials = [];

        for (const key of this.types) {
            const group = data.find((d) => d.type === key);

            if (group) {
                const groupGeoms = [];
                for (const d of group.objects) {
                    groupGeoms.push(this.modifyGeometry(group.type, getOnlyGeometryFromRawData(d)));
                }

                if (groupGeoms.length > 0) {
                    geoms.push(groupGeoms);
                    materials.push(this.materials[key].material);
                    strokeMaterials.push(this.materials[key].stroke);
                }
            }
        }

        const mergedGeoms: THREE.BufferGeometry[] = [];
        for (const g of geoms) {
            mergedGeoms.push(mergeGeometries(g));
            g.forEach((b) => b.dispose());
        }

        if (mergedGeoms.length === 0) {
            return {
                meshes: new THREE.Mesh(),
                strokes: new THREE.LineSegments(),
            };
        }

        const mergedEdges = mergedGeoms.map((g) => new THREE.EdgesGeometry(g, thresholdAngle));

        const meshes = mergeGeometries(mergedGeoms, true);
        meshes.computeVertexNormals();

        const strokes = mergeGeometries(mergedEdges, true);

        return {
            meshes: new THREE.Mesh(meshes, materials),
            strokes: new THREE.LineSegments(strokes, strokeMaterials),
        };
    }

    protected modifyGeometry(_type: PreProcessingTypes, geom: THREE.BufferGeometry): THREE.BufferGeometry {
        return geom;
    }

    override setMode(mode: GeoObjectMode) {
        super.setMode(mode);
    }

    protected override onStandardMode(): void {
        const showStrokes = getGlobalState().ambient.showStrokes;
        this.setMaterialMode(false, false);
        this.meshes.visible = true;
        this.strokes.visible = showStrokes;
    }

    protected onRenderMode(): void {
        const showStrokes = getGlobalState().ambient.showStrokes;
        this.setMaterialMode(true, true);
        this.meshes.visible = true;
        this.strokes.visible = showStrokes;
    }

    private setMaterialMode(useShadedMaterial: boolean, enableShadows: boolean): void {
        const activeMaterials = this.types.map((type) =>
            useShadedMaterial ? this.materials[type].shadowMaterial : this.materials[type].material
        );
        this.meshes.material = activeMaterials;

        this.meshes.castShadow = enableShadows;
        this.meshes.receiveShadow = enableShadows;
    }
}
